// =============================================================================
// CONTACT PAGE COMPONENT
// =============================================================================

import React, { useState } from 'react';
import { Navbar, Footer } from '@/components/Layout';
import { ContentRenderer } from '@/components/ContentRenderer';
import { ContactForm } from '@/components/Form';
import { ScheduleCallForm } from '@/components/Form/ScheduleCallForm';
import { SEOHead } from '@/components/SEO';
import { LoadingPage, ErrorPage } from '@/components/LoadingPage';
import { usePageData } from '@/hooks/usePageData';
import { seoData } from '@/data/seoData';
import { navItems as defaultNavItems, footerLinks as defaultFooterLinks, socialLinks as defaultSocialLinks } from '@/data/homeContent';

export const ContactPage: React.FC = () => {
  // Load page data from Firebase Storage with fallback to local
  const { data, loading, error, reload } = usePageData('contact');
  const [formType, setFormType] = useState<'contact' | 'schedule'>('contact');

  // Show loading state
  if (loading) {
    return <LoadingPage message="Loading contact page..." />;
  }

  // Show error state with retry
  if (error) {
    return <ErrorPage error={error} onRetry={reload} />;
  }

  // No data available
  if (!data) {
    return <ErrorPage error={new Error('No data available')} onRetry={reload} />;
  }

  const seo = data.seo || seoData.contact;
  const navItems = data.navigation?.navItems || defaultNavItems;
  const footerLinks = data.navigation?.footerLinks || defaultFooterLinks;
  const socialLinks = data.navigation?.socialLinks || defaultSocialLinks;
  
  const handleFormSubmit = (_data: unknown) => {
    // Form submission is handled by the ContactForm component
    // This callback can be used for additional analytics or tracking
    // TODO: Add analytics tracking here if needed
  };

  const handleScheduleSuccess = () => {
    // Handle successful call scheduling
    // Could add analytics tracking here
  };

  // Handle SEO data safely
  const seoTitle = typeof seo.title === 'string' ? seo.title : seo.title;
  const seoKeywords = Array.isArray(seo.keywords) ? seo.keywords.join(', ') : seo.keywords;
  const seoCanonical = 'canonicalUrl' in seo ? (seo as any).canonicalUrl : undefined;
  const seoStructured = 'structuredData' in seo ? (seo as any).structuredData : undefined;

  return (
    <div className="contact-page">
      <SEOHead
        title={seoTitle}
        description={seo.description}
        keywords={seoKeywords}
        canonicalUrl={seoCanonical}
        structuredData={seoStructured}
      />
      
      <Navbar 
        brand="Alex Finger Consulting"
        navItems={navItems}
        showThemeSwitcher={false}
      />
      
      <main className="main-content">
        <ContentRenderer components={data.components} />
        
        <section className="contact-form-section section-padding">
          <div className="container">
            <div className="contact-form-wrapper">
              <div className="form-intro">
                <h2>Get In Touch</h2>
                <p>Choose how you'd like to connect with me.</p>
                
                <div className="form-type-selector">
                  <button
                    className={`form-type-btn ${formType === 'contact' ? 'active' : ''}`}
                    onClick={() => setFormType('contact')}
                  >
                    Send Message
                  </button>
                  <button
                    className={`form-type-btn ${formType === 'schedule' ? 'active' : ''}`}
                    onClick={() => setFormType('schedule')}
                  >
                    Schedule a Call
                  </button>
                </div>
              </div>
              
              {formType === 'contact' ? (
                <ContactForm onSubmit={handleFormSubmit} />
              ) : (
                <ScheduleCallForm onSuccess={handleScheduleSuccess} />
              )}
            </div>
          </div>
        </section>
      </main>
      
      <Footer
        brand="Alex Finger Consulting"
        description="Web development and business strategy services. Transform your ideas into digital reality."
        links={footerLinks}
        socialLinks={socialLinks}
      />
    </div>
  );
};
